import * as React from "react";
import { useForm } from "react-hook-form";
import styled, { css } from "styled-components";
import * as _ from "lodash";
import { toast } from "react-toastify";

import Grid from "src/library/components/Hedron";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text/Text";
import { FormRow } from "src/library/components/Forms";
import Input from "src/library/components/Forms/Input";
import { Button } from "src/library/components/Button";
import { IAddress } from "src/library/components/Address";
import {
  GetSettingParams,
  CreateSettingParams,
  useInheritedSettingsApi
} from "src/core/shared/api/inheritedSettings.api";
import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  emailRegex,
  httpsWebsiteRegex,
  intlPhoneRegex
} from "src/utils/validation/regex";
import PointOfContactDisplay from "src/modules/support/components/PointOfContactDisplay";
import {
  IInheritedSetting,
  PointOfContact
} from "src/core/drops/api/drops.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

interface FormValues {
  contactName?: string;
  contactEmail?: string;
  contactNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  postcode?: string;
  website?: string;
}

/**
 * Support settings page
 *
 * @returns
 */
const SupportSettingsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const [ contactSettingRes, contactSettingReq ] = useInheritedSettingsApi<"GET_GLOBAL_SETTING", GetSettingParams, IInheritedSetting<"point_of_contact">>("GET_GLOBAL_SETTING");
  const [ updateContactSettingRes, updateContactSettingReq ] = useInheritedSettingsApi("UPDATE_SETTING");
  const [ createContactSettingRes, createContactSettingReq ] = useInheritedSettingsApi<"CREATE_SETTING", CreateSettingParams, IInheritedSetting<"point_of_contact">>("CREATE_SETTING");
  const [ initialSetting, setInitialSetting ] = React.useState<IInheritedSetting<"point_of_contact">>();
  const checkPermission = useCheckUserPermission();

  const {
    reset, register, handleSubmit, errors, watch
  } = useForm<FormValues>({});

  React.useEffect(() => {
    contactSettingReq({ name: "point_of_contact" });
  }, [ contactSettingReq ]);

  React.useEffect(() => {
    if (contactSettingRes.data) {
      setInitialSetting(contactSettingRes.data);
      const initialValues = contactSettingRes.data.value;

      // set the forms initial values
      // @ts-ignore
      reset({
        contactName: initialValues.contactName,
        contactEmail: initialValues.contactEmail,
        contactNumber: initialValues.contactNumber,
        addressLine1: initialValues.address && initialValues.address.addressLine1,
        addressLine2: initialValues.address && initialValues.address.addressLine2,
        addressLine3: initialValues.address && initialValues.address.addressLine3,
        city: initialValues.address && initialValues.address.city,
        postcode: initialValues.address && initialValues.address.postcode,
        website: initialValues.website
      });
    }

    // allow a 404 as they may not have set this up yet!
    if (contactSettingRes.error && contactSettingRes.error.status !== 404) {
      toast.error(contactSettingRes.error.message);
    }
  }, [ contactSettingRes.data, contactSettingRes.error ]);

  const canAmend = checkPermission("amend-global-settings");
  const formValues = watch();

  const currentDisplayValues: PointOfContact = React.useMemo(() => {
    const address: IAddress = {
      addressLine1: formValues.addressLine1 as string,
      addressLine2: formValues.addressLine2,
      addressLine3: formValues.addressLine3,
      city: formValues.city,
      postcode: formValues.postcode as string
    };

    const addressEmpty = _.every(address, _.isEmpty);

    const settingValue: PointOfContact = {
      contactName: formValues.contactName,
      contactNumber: formValues.contactNumber,
      contactEmail: formValues.contactEmail,
      website: formValues.website
    };

    // only add the address to the json object if its been set
    if (!addressEmpty) {
      settingValue.address = address;
    }

    return settingValue;
  }, [ formValues ]);

  /**
   * Save the form, either create or update the setting
   */
  const saveForm = React.useCallback((_values: FormValues) => {
    if (initialSetting && initialSetting.id) {
      updateContactSettingReq({
        ...initialSetting,
        value: currentDisplayValues
      });
    } else {
      createContactSettingReq({
        name: "point_of_contact",
        scope: "global",
        value: currentDisplayValues
      });
    }
  }, [
    initialSetting,
    updateContactSettingReq,
    currentDisplayValues,
    createContactSettingReq
  ]);

  // handle create reponse
  React.useEffect(() => {
    if (createContactSettingRes.data) {
      setInitialSetting(createContactSettingRes.data);
      toast.success("Saved successfully");
    }

    if (createContactSettingRes.error) {
      toast.error(createContactSettingRes.error.message);
    }
  }, [ createContactSettingRes ]);

  // handle update reponse
  React.useEffect(() => {
    if (updateContactSettingRes.data) {
      setInitialSetting(updateContactSettingRes.data);
      toast.success("Saved successfully");
    }

    if (updateContactSettingRes.error) {
      toast.error(updateContactSettingRes.error.message);
    }
  }, [ updateContactSettingRes ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <PageInnerWrapper>
          <Grid.Box className="page-left">
            <Grid.Bounds direction="vertical">
              <Text as="h2">
                Point of contact
              </Text>

              {contactSettingRes.pending && <LoadingSpinner type="circular" />}

              <FormWrapper onSubmit={handleSubmit(values => saveForm(values))}>
                <FormRow>
                  <Input
                    label="Contact Name"
                    as="input"
                    name="contactName"
                    disabled={!canAmend}
                    ref={register({ required: false })}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    label="Contact Email"
                    as="input"
                    name="contactEmail"
                    error={errors.contactEmail && errors.contactEmail.message}
                    disabled={!canAmend}
                    ref={register({
                      required: false,
                      pattern: {
                        value: emailRegex,
                        message: "Email must be a valid email address"
                      }
                    })}
                  />

                </FormRow>

                <FormRow>
                  <Input
                    label="Contact Number"
                    as="input"
                    name="contactNumber"
                    error={errors.contactNumber && errors.contactNumber.message}
                    disabled={!canAmend}
                    ref={register({
                      required: false,
                      pattern: {
                        value: intlPhoneRegex,
                        message: "Must be a valid telephone number"
                      }
                    })}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    label="Website"
                    as="input"
                    name="website"
                    error={errors.website && errors.website.message}
                    disabled={!canAmend}
                    ref={register({
                      pattern: {
                        value: httpsWebsiteRegex,
                        message: "Must be a valid HTTPS URL"
                      },
                      required: false
                    })}
                  />
                </FormRow>

                <Text as="h3">
                  Address
                </Text>

                <FormRow>
                  <Input
                    label="Address line 1"
                    as="input"
                    name="addressLine1"
                    ref={register({ required: false })}
                    disabled={!canAmend}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    label="Address line 2"
                    as="input"
                    name="addressLine2"
                    ref={register({ required: false })}
                    disabled={!canAmend}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    label="Address line 3"
                    as="input"
                    name="addressLine3"
                    ref={register({ required: false })}
                    disabled={!canAmend}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    label="City"
                    as="input"
                    name="city"
                    ref={register({ required: false })}
                    disabled={!canAmend}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    label="Postcode"
                    as="input"
                    name="postcode"
                    ref={register({ required: false })}
                    disabled={!canAmend}
                  />
                </FormRow>

                <FormRow>
                  <Button
                    type="submit"
                    $buttonType="primary"
                    disabled={!canAmend}
                  >
                    Save
                  </Button>
                </FormRow>
              </FormWrapper>
            </Grid.Bounds>
          </Grid.Box>

          <Grid.Box className="page-right">
            <PointOfContactDisplay
              mode="preview"
              contact={currentDisplayValues}
            />
          </Grid.Box>
        </PageInnerWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

export default SupportSettingsPage;

const PageInnerWrapper = styled(Grid.Bounds)`
  ${() => css`
    .page-left {
      min-width: 500px;
    }

    .page-right {
      min-width: 300px;
    }
  `}
`;

const FormWrapper = styled.form`
  ${({ theme }) => css`
    h3 {
      margin-top: ${theme.spacing.margins.xl};
    }

  `}
`;
